import { Link, NavLink, Outlet, useNavigate } from 'react-router-dom';
import { LayoutDashboard, Users, UserCog, FileCheck, MessageSquare, Newspaper, LogOut, Building2, Wallet, BellRing, Home, BarChart3, Calendar, Handshake, Gift, Info, GraduationCap } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { LOGO_DARK_BG } from '../../lib/constants';
import NotificationBell from '../../components/NotificationBell';

const NAV = [
  { to: '/admin', label: 'Panel', icon: LayoutDashboard, end: true, testid: 'admin-nav-dashboard' },
  { to: '/admin/analytics', label: 'Analytics', icon: BarChart3, testid: 'admin-nav-analytics' },
  { to: '/admin/usuarios', label: 'Usuarios y roles', icon: UserCog, testid: 'admin-nav-users' },
  { to: '/admin/socios', label: 'Socios', icon: Users, testid: 'admin-nav-members' },
  { to: '/admin/solicitudes', label: 'Solicitudes', icon: FileCheck, testid: 'admin-nav-requests' },
  { to: '/admin/propiedades', label: 'Propiedades', icon: Building2, testid: 'admin-nav-properties' },
  { to: '/admin/eventos', label: 'Eventos', icon: Calendar, testid: 'admin-nav-events' },
  { to: '/admin/capacitaciones', label: 'Capacitaciones', icon: GraduationCap, testid: 'admin-nav-trainings' },
  { to: '/admin/partners', label: 'Partners & Ads', icon: Handshake, testid: 'admin-nav-partners' },
  { to: '/admin/beneficios', label: 'Beneficios', icon: Gift, testid: 'admin-nav-benefits' },
  { to: '/admin/nosotros', label: 'Página Nosotros', icon: Info, testid: 'admin-nav-about' },
  { to: '/admin/facturacion', label: 'Facturación', icon: Wallet, testid: 'admin-nav-billing' },
  { to: '/admin/notificaciones', label: 'Notificaciones', icon: BellRing, testid: 'admin-nav-broadcast' },
  { to: '/admin/home', label: 'Portada (Home)', icon: Home, testid: 'admin-nav-home-config' },
  { to: '/admin/mensajes', label: 'Mensajes', icon: MessageSquare, testid: 'admin-nav-messages' },
  { to: '/admin/noticias', label: 'Noticias', icon: Newspaper, testid: 'admin-nav-news' },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate('/', { replace: true });
  };

  return (
    <div className="min-h-screen bg-slate-100" data-testid="admin-layout">
      <aside className="fixed inset-y-0 left-0 z-40 hidden w-64 flex-col bg-cnib-navy lg:flex">
        <Link to="/" className="flex h-20 items-center gap-3 px-6">
          <img src={LOGO_DARK_BG} alt="CNIB" className="h-11 w-auto shrink-0 object-contain" />
          <span className="rounded-full border border-orange-500/50 bg-orange-500/10 px-2 py-0.5 text-[9px] font-bold uppercase tracking-[0.18em] text-orange-400">
            Admin
          </span>
        </Link>
        <nav className="flex-1 space-y-1 px-3 py-4">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              data-testid={item.testid}
              className={({ isActive }) =>
                `flex items-center gap-3 rounded-xl px-4 py-3 text-sm font-semibold transition-colors ${
                  isActive ? 'bg-orange-500 text-white' : 'text-slate-300 hover:bg-white/5 hover:text-white'
                }`
              }
            >
              <item.icon className="h-5 w-5" />
              {item.label}
            </NavLink>
          ))}
        </nav>
        <div className="border-t border-white/10 p-4">
          <div className="mb-3 px-2">
            <p className="truncate text-sm font-bold text-white">{user?.name}</p>
            <span className="mt-1 inline-block rounded-full bg-orange-500/15 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-orange-400">
              {user?.role === 'superadmin' ? 'Superadmin' : 'Admin'}
            </span>
          </div>
          <button
            onClick={handleLogout}
            data-testid="admin-logout-button"
            className="flex w-full items-center gap-3 rounded-xl px-4 py-2.5 text-sm font-semibold text-slate-300 transition-colors hover:bg-white/5 hover:text-orange-400"
          >
            <LogOut className="h-4 w-4" /> Cerrar sesión
          </button>
        </div>
      </aside>

      <header className="sticky top-0 z-30 flex h-16 items-center justify-between border-b border-slate-200 bg-white px-4 lg:ml-64 lg:px-8">
        <Link to="/" className="lg:hidden">
          <img src={LOGO_DARK_BG} alt="CNIB" className="h-8 w-auto rounded bg-cnib-navy p-1" />
        </Link>
        <p className="hidden font-heading text-lg font-bold text-slate-900 lg:block">Panel administrativo</p>
        <div className="flex items-center gap-3">
          <NotificationBell />
          <span className="flex h-9 w-9 items-center justify-center rounded-full bg-orange-500 font-heading text-sm font-bold text-white">
            {user?.name?.charAt(0) || 'A'}
          </span>
        </div>
      </header>

      <main className="px-4 pb-24 pt-6 lg:ml-64 lg:px-8 lg:pb-10">
        <Outlet />
      </main>

      <nav className="fixed inset-x-0 bottom-0 z-40 flex items-center justify-around overflow-x-auto border-t border-slate-200 bg-white py-2 lg:hidden">
        {NAV.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            className={({ isActive }) =>
              `flex flex-col items-center gap-1 rounded-lg px-3 py-1.5 text-[10px] font-bold ${
                isActive ? 'text-orange-500' : 'text-slate-400'
              }`
            }
          >
            <item.icon className="h-5 w-5" />
            {item.label}
          </NavLink>
        ))}
      </nav>
    </div>
  );
}
